import { useEffect, useState } from "react";
import Link from "next/link";
import PropTypes from "prop-types";
import FilterAccordion from "./FilterAccordion";
import FilterItems from "./FilterItems";
import {
  formatRedirectionUrl,
  hanldeCategoryInitiateNavigation,
} from "./helper";
import { DEFAULT_VISIBLE_FILTERS } from "@/constants";
import { useSearchParams } from "next/navigation";
import { useContentfulTranslations } from "@/utils/contentfulTranslations";
import { getPrefixLocale } from "@/utils/helper";

const specRegional = "SpecRegionProductCategoryLocal";
const regional = "RegionProductCategoryLocal";

const ResultLinkButton = ({
  pageType,
  totalProductCount,
  locale = "en",
  slug = "/",
  categoryConfig = "",
  searchValue = "",
}) => {
  const slugUrl = Array.isArray(slug) ? slug.join("/") : slug;

  return (
    <a
      href={hanldeCategoryInitiateNavigation(
        locale,
        pageType,
        slugUrl,
        searchValue,
        categoryConfig
      )}
    >
      <button className="cursor-pointer m-0 mb-[10px] outline-none bg-[#e5e5e5] text-gray-800 inline-block border-0 rounded-md relative text-left w-full pb-[10px]">
        <span className="inline-block text-base leading-[13px] font-[400] shadow-none font-['HelveticaBold',_Helvetica,_sans-serif] mr-[5px] mt-[15px] ml-[10px] text-[13px]">
          {categoryConfig}
        </span>
        <span className="inline-block font-bold leading-none overflow-visible pr-2 mr-[-10px] mt-[-10px] text-[13px] font-[Helvetica-Light,_Helvetica,_sans-serif] text-[#999]">
          {`(${totalProductCount}) `}&nbsp;
        </span>
        <span className="absolute mt-[10px] right-2 text-gray-400 font-normal text-base">
          x
        </span>
      </button>
    </a>
  );
};

const CategoryListDesktop = ({
  categoryArr,
  pageType,
  searchValue,
  currentPageValue,
}) => (
  <ul className="m-0 mb-[10px] outline-none relative text-left w-full">
    {categoryArr?.map((item, index) => (
      <li className="py-[16px] cursor-pointer" key={item.value || index}>
        <a
          href={formatRedirectionUrl(
            item.value,
            pageType,
            searchValue,
            currentPageValue
          )}
          className="font-helveticaLight text-[15px] leading-tight font-light text-[#232323] hover:no-underline"
        >
          {item.value} ({item.count})
        </a>
      </li>
    ))}
  </ul>
);

export default function FilterContent({
  filterOptions,
  slug = "/",
  filterSelect,
  selectedFilter,
  selectedFilterCount,
  showAll,
  pageType = "plp",
  searchValue,
  currentPageValue,
  locale,
  totalProductCount,
  setSelectedFilterCount,
  categoryConfig = "",
  search = "",
  categoryArr = [],
  activeTab = "",
}) {
  const { staticLabelsPLP } = useContentfulTranslations();
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [openAccordions, setOpenAccordions] = useState([]);

  const slugUrl = Array.isArray(slug) ? slug.join("/") : slug;

  const toggleMobileFilter = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  const getResultLink = () => {
    const slugUrl = Array.isArray(slug) ? slug.join("/") : slug;
    return pageType === "plp" ? slugUrl : `results?search=${search}`;
  };

  const linkProps = getResultLink();

  const generateLinkProps = () => {
    const slugUrl = Array.isArray(slug) ? slug.join("/") : slug;
    return pageType === "plp"
      ? slugUrl.split("/")[0]
      : `/results?search=${search}`;
  };

  const linkPropsShowAll = generateLinkProps();

  const filterEntries = Object.entries(filterOptions);
  const visibleFilterEntries = showAllFilters
    ? filterEntries
    : filterEntries.slice(0, DEFAULT_VISIBLE_FILTERS);

  useEffect(() => {
    if (selectedFilter) {
      const selectedKeys = Object.keys(selectedFilter);
      setOpenAccordions(prevOpen => {
        // Keep manually opened accordions open, add new selected ones
        const newOpen = [...prevOpen];
        selectedKeys.forEach(key => {
          if (!newOpen.includes(key)) newOpen.push(key);
        });
        return newOpen;
      });
    }
  }, [selectedFilter]);

  const handleAccordionToggle = label => {
    setOpenAccordions(prevOpen =>
      prevOpen.includes(label)
        ? prevOpen.filter(l => l !== label)
        : [...prevOpen, label]
    );
  };

  const handleReloadNavigation = e => {
    e.preventDefault();
    const currentParams = new URLSearchParams(window.location.search);
    const filterObj = Object.fromEntries(currentParams.entries());
    const hasDynamicFilters = Object.keys(filterObj).some(rawKey => {
      const cleanKey = rawKey.replace(/\+/g, " ").trim();
      const value = filterObj[rawKey];

      return (
        cleanKey !== "type" &&
        cleanKey !== "search" &&
        value !== "" &&
        value !== null &&
        value !== undefined
      );
    });

    if (!hasDynamicFilters) return;

    const fullUrl = e.currentTarget.href;

    const prefixLocale = getPrefixLocale(locale);

    const cleanUrl =
      pageType === "plp"
        ? fullUrl.split(/[?#]/)[0]
        : `${prefixLocale}results?search=${search}`;

    if (pageType === "search" && categoryConfig) {
      const { type = "", search = "" } = filterObj;
      globalThis.location.href =
        window.location.pathname + `?type=${type}&search=${search}`;
    } else {
      globalThis.location.href = cleanUrl;
    }
  };

  const handleFilterSelect = (obj1 = {}, obj2 = {}) => {
    toggleMobileFilter();
    filterSelect(obj1, obj2);
  };

  return (
    <>
      <div className="flex flex-col pl-[10px] w-1/4 mb-[38px] hidden md:block">
        <div className="relative mb-8 border-b-1 border-[#bebebe]">
          <div className="flex justify-between pb-[22px] mt-[14px] mb-[22px]  border-b-1 border-[#bebebe]">
            <div className="flex">
              <h3 className="font-['Helvetica-Light',_Helvetica,_sans-serif] leading-tight text-[22px] font-normal">
                {staticLabelsPLP[locale]?.category}
              </h3>
            </div>
            {categoryConfig ? (
              <div className="flex mt-[5px] text-[13px]">
                <a
                  href={hanldeCategoryInitiateNavigation(
                    locale,
                    pageType,
                    slugUrl,
                    search,
                    categoryConfig
                  )}
                  className="font-['Helvetica-Light',_Helvetica,_sans-serif] leading-relaxed text-[13px] font-normal text-[#3b659c] ![text-decoration:none] cursor-pointer"
                >
                  {staticLabelsPLP[locale]?.showAll}
                </a>
              </div>
            ) : null}
          </div>
          {slug ? (
            <ResultLinkButton
              pageType={pageType}
              selectedFilter={selectedFilter}
              totalProductCount={totalProductCount}
              locale={locale}
              slug={slug}
              searchValue={search}
              categoryConfig={categoryConfig}
            />
          ) : (
            <CategoryListDesktop
              categoryArr={categoryArr}
              pageType={pageType}
              searchValue={search}
              currentPageValue={currentPageValue}
            />
          )}
        </div>
        {!showAll && (
          <>
            <div className="flex justify-between pb-[22px] mt-[14px] mb-[22px] border-b-1">
              <div className="flex">
                <h3 className="font-['Helvetica-Light',_Helvetica,_sans-serif] leading-tight text-[22px] font-normal">
                  {staticLabelsPLP[locale]?.filterby}
                </h3>
              </div>
              <div className="flex mt-[5px] text-[#3b659c] text-[13px]">
                <Link
                  href={linkProps}
                  as={linkProps}
                  onClick={handleReloadNavigation}
                  className="![text-decoration:none] cursor-pointer"
                >
                  {staticLabelsPLP[locale]?.clearAll}
                </Link>
              </div>
            </div>
            {visibleFilterEntries.map(
              ([label, { field, options, type = "" }]) => {
                let filterTypeValue =
                  type || (field === "ColorFinish_s" ? "image" : "list");
                return (
                  <FilterAccordion
                    header={label}
                    key={label}
                    isOpen={openAccordions.includes(label)}
                    onToggle={() => handleAccordionToggle(label)}
                  >
                    <FilterItems
                      key={label}
                      filterOption={options}
                      filterType={filterTypeValue}
                      filterName={label}
                      filterField={field}
                      minRange={0}
                      maxRange={0}
                      filterSelect={filterSelect}
                      selectedFilter={selectedFilter}
                      selectedFilterCount={selectedFilterCount}
                      totalProductCount={totalProductCount}
                    />
                  </FilterAccordion>
                );
              }
            )}
            {filterEntries.length > DEFAULT_VISIBLE_FILTERS && (
              <button
                className="cursor-pointer text-[#3b659c] text-sm mt-2"
                onClick={() => setShowAllFilters(prev => !prev)}
              >
                {showAllFilters
                  ? staticLabelsPLP[locale]?.showLess
                  : staticLabelsPLP[locale]?.showMore}
              </button>
            )}
          </>
        )}
      </div>
      <div className="block appearance-none w-full py-[10px] px-[10px] pr-[10px] rounded leading-normal focus:outline-none focus:bg-white focus:border-gray-500 md:hidden">
        {/* Mobile filter toggle button */}
        <button
          className="m-0 mb-[20px] text-[16px] outline-none bg-white text-[#232323] inline-block border border-solid border-gray-300 rounded-md font-[400] leading-[16px] text-center w-full py-5 px-8 cursor-pointer font-[helveticaRoman,_Helvetica,_sans-serif]"
          onClick={toggleMobileFilter}
        >
          <span>{staticLabelsPLP[locale]?.buttonMDLabel}</span>
        </button>

        {/* Mobile filter content */}
        {isMobileFilterOpen && (
          <div className="bg-white p-[4x] md:hidden">
            <div className="flex justify-between pb-[22px] mt-[14px] mb-[22px] border-b-2">
              <div className="flex">
                <h3 className="font-helveticaLight leading-tight text-xl font-normal">
                  {staticLabelsPLP[locale]?.category}
                </h3>
              </div>
              <div className="flex mt-[5px]">
                <a
                  href={hanldeCategoryInitiateNavigation(
                    locale,
                    pageType,
                    slugUrl,
                    search,
                    categoryConfig
                  )}
                  className="font-helveticaLight leading-relaxed text-[14px] font-normal"
                >
                  {staticLabelsPLP[locale]?.showAll}
                </a>
              </div>
            </div>
            <div className="mb-[5px] pr-[30px] border-b-2">
              {slug ? (
                <ResultLinkButton
                  pageType={pageType}
                  selectedFilter={selectedFilter}
                  totalProductCount={totalProductCount}
                  locale={locale}
                  slug={slug}
                  categoryConfig={categoryConfig}
                  searchValue={search}
                />
              ) : (
                <CategoryListDesktop
                  categoryArr={categoryArr}
                  pageType={pageType}
                  searchValue={search}
                  currentPageValue={currentPageValue}
                />
              )}
            </div>
            <div className="flex justify-between pb-[22px] mt-[14px] mb-[22px] border-b-2">
              <div className="flex">
                <h3 className="font-helveticaLight leading-tight text-xl font-normal">
                  {staticLabelsPLP[locale]?.filterby}
                </h3>
              </div>
              <div className="flex mt-[5px]">
                <Link
                  href={linkProps}
                  as={linkProps}
                  onClick={handleReloadNavigation}
                >
                  {staticLabelsPLP[locale]?.clearAll}
                </Link>
              </div>
            </div>
            {visibleFilterEntries.map(
              ([label, { field, options, type = "" }]) => {
                let filterTypeValue =
                  type || (field === "ColorFinish_s" ? "image" : "list");
                return (
                  <FilterAccordion
                    header={label}
                    key={label}
                    isOpen={openAccordions.includes(label)}
                    onToggle={() => handleAccordionToggle(label)}
                  >
                    <FilterItems
                      key={label}
                      filterOption={options}
                      filterType={filterTypeValue}
                      filterName={label}
                      filterField={field}
                      minRange={0}
                      maxRange={0}
                      filterSelect={handleFilterSelect}
                      selectedFilter={selectedFilter}
                      selectedFilterCount={selectedFilterCount}
                      totalProductCount={totalProductCount}
                    />
                  </FilterAccordion>
                );
              }
            )}
            {filterEntries.length > DEFAULT_VISIBLE_FILTERS && (
              <button
                className="cursor-pointer text-[#3b659c] text-sm mt-2"
                onClick={() => setShowAllFilters(prev => !prev)}
              >
                {showAllFilters ? "Show Less Filters" : "Show More Filters"}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

FilterContent.propTypes = {
  filterOptions: PropTypes.shape({
    RegionProductCategoryLocal: PropTypes.array,
    SpecRegionProductCategoryLocal: PropTypes.array,
  }),
  slug: PropTypes.string,
  slug: PropTypes.string,
  filterSelect: PropTypes.func,
  selectedFilter: PropTypes.shape({
    search: PropTypes.string,
    RegionProductCategoryLocal: PropTypes.string,
  }),
  selectedFilterCount: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  showAll: PropTypes.bool,
  pageType: PropTypes.string,
  searchValue: PropTypes.string,
  currentPageValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  locale: PropTypes.string,
  totalProductCount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setSelectedFilterCount: PropTypes.func,
};

ResultLinkButton.propTypes = {
  pageType: PropTypes.string,
  selectedFilter: PropTypes.array,
  locale: PropTypes.string,
  slug: PropTypes.string,
  slug: PropTypes.string,
  totalProductCount: PropTypes.string,
};

CategoryListDesktop.propTypes = {
  categoryArr: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })),
  pageType: PropTypes.string,
  searchValue: PropTypes.string,
  currentPageValue: PropTypes.string,
};

FilterContent.defaultProps = {
  filterOptions: {
    response: {
      "@search.facets": "",
    },
  },
  slug: "",
  slug: "",
  filterSelect: () => {},
  selectedFilter: {},
  selectedFilterCount: "",
};
